import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import { ResponsiveImage } from "@quintype/components";
import { HeroBanner } from "../../molecules/HeroBanner";
import { LocalNavBar } from "../../molecules/LocalNavBar";
import { LargeTextParagraph } from "../../atoms/LargeTextParagraph";
import { HighlightCardSection } from "../../molecules/HighlightCardSection";
import { SpeakersSection } from "../../molecules/SpeakersSection";
import { AgendaSection } from "../../molecules/AgendaSection";
import { SponsorsSection } from "../../molecules/SponsorsSection";
import { ParticipantsSection } from "../../molecules/ParticipantsSection";
import { ParticipantsListSection } from "../../molecules/ParticipantsListSection";
import { BookSeatSection } from "../../molecules/BookSeatSection";
import { EventDetailsSection } from "../../molecules/EventDetailsSection";
import { AdBanner } from "../../atoms/AdBanner";
import { EventGallery } from "../../molecules/EventGallery";
import { EventOutcomes } from "../../molecules/EventOutcomes";
import { KeyTakeawaysCard } from "../../molecules/KeyTakeawaysCard";
import { SignInModal } from "../../layouts/header/sign-in-modal";
import { KeepMeInformedModal } from "../../molecules/KeepMeInformedModal";
import { RsvpModal } from "../../molecules/RsvpModal";
import { CONFERENCE_LOCAL_NAV_DATA, PREVIOUS_CONFERENCE_LOCAL_NAV_DATA, SESSIONS_LOCAL_NAV_DATA, PREVIOUS_SESSIONS_LOCAL_NAV_DATA } from "../../../constants";
import "./conference-page.m.css";
import useIsMobile from "../../../hooks/useIsMobile";
import { processHtmlLinks } from "../../../utils/html-utils";

// Helper function to style bold text with red color and wrap non-bold text
const styleBoldText = (html) => {
  if (!html) return "";
  // Replace <p> tags with divs to preserve paragraph breaks
  let content = html.replace(/<p>/gi, '<div style="margin-bottom: 30px;">').replace(/<\/p>/gi, '</div>');
  // Remove margin-bottom from the last div
  content = content.replace(/(<div[^>]*margin-bottom: 30px;[^>]*>(?:(?!<div).)*?<\/div>)(?![\s\S]*<div[^>]*margin-bottom: 30px;)/,
    (match) => match.replace('margin-bottom: 30px;', ''));
  // Replace <strong> and <b> tags with red bold styled spans
  // and wrap the remaining text after </strong> or </b> with normal styled span
  content = content
    .replace(/<strong>/gi, '<span style="font-weight: 600; color: #ff2626;">')
    .replace(/<\/strong>/gi, '</span><span style="font-weight: 400; color: #242424;">')
    .replace(/<b>/gi, '<span style="font-weight: 600; color: #ff2626;">')
    .replace(/<\/b>/gi, '</span><span style="font-weight: 400; color: #242424;">')
    .replace(/<a([^>]*)>/gi, '<a$1 style="color: #ff2626;">');
  // Close the trailing span if content doesn't end with a closing span
  if (!content.trim().endsWith('</span>')) {
    content += '</span>';
  }
  let processedHtml = processHtmlLinks(content);
  return processedHtml;
};

const extractHighlightCardDataWithGallery = (card) => {
  const storyElements = card?.['story-elements'] || [];
  const textElements = storyElements.filter((el) => el?.type === "text" && el?.subtype !== "cta");

  // Find image gallery - check top level or inside composite elements
  let imageGalleryElement = storyElements.find((el) => el?.subtype === "image-gallery");
  if (!imageGalleryElement) {
    const compositeElement = storyElements.find((el) => el?.type === "composite");
    imageGalleryElement = compositeElement?.['story-elements']?.find((el) => el?.subtype === "image-gallery");
  }

  let galleryImages = imageGalleryElement?.['story-elements'] || [];

  // Find CTA element for "More photos" link
  const ctaElement = storyElements.find((el) => el?.subtype === "cta");
  const morePhotosUrl = ctaElement?.metadata?.['cta-url'] || card?.metadata?.attributes?.['more-photos-url']?.[0] || "#";
  const morePhotosText = ctaElement?.metadata?.["cta-title"] || ctaElement?.metadata?.["cta-text"] || ctaElement?.text || "More Photos";
  const openInNewTab = ctaElement?.metadata?.["open-in-new-tab"] || false;

  return {
    storyElements: textElements,
    galleryImages,
    hasGallery: galleryImages.length > 0,
    morePhotosUrl,
    morePhotosText,
    openInNewTab,
  };
};

const extractOutcomeCardData = (card) => {
  const storyElements = card?.['story-elements'] || [];

  const alsoReadElement = storyElements.find((el) => el.subtype === "also-read");
  const titleElements = storyElements.filter((el) => el.type === "title" && el.subtype !== "question");
  const firstTitle = titleElements[0]?.text || "";
  const secondTitle = titleElements[1]?.text || "";
  // Extract all text elements (non-question, non-answer)
  const textElements = storyElements.filter(
    (el) => el.type === "text" && el.subtype !== "question" && el.subtype !== "answer" && el.subtype !== "also-read"
  );

  const descriptionText = textElements[0]?.text || "";
  const footnoteText = textElements[1]?.text || textElements.find(t => t.text && t.text.trim().startsWith("*"))?.text || "";

  let conclusionPaper = null;
  if (descriptionText || alsoReadElement) {
    conclusionPaper = {
      title: firstTitle,
      description: descriptionText,
      url: alsoReadElement?.metadata?.["linked-story-slug"]
        ? `/${alsoReadElement.metadata["linked-story-slug"]}`
        : alsoReadElement?.metadata?.["linked-story"]?.slug
          ? `/${alsoReadElement.metadata["linked-story"].slug}`
          : (alsoReadElement?.url || "#")
    };
  }

  const questionElements = storyElements.filter((el) => el.subtype === "question");
  const answerElements = storyElements.filter((el) => el.subtype === "answer");
  const fileElements = storyElements.filter((el) => el.type === "file");

  const presentations = [];

  if (questionElements.length > 0) {
    // New CMS structure: separate question and answer elements
    questionElements.forEach((q, idx) => {
      const file = fileElements[idx];
      // Collect all answers as an array of strings
      const speakers = answerElements
        .map((a) => a.text || a.metadata?.answer || "")
        .filter(Boolean);
      presentations.push({
        title: q.text || q.metadata?.question || "",
        speakers,
        pdfUrl: file?.url || file?.metadata?.url || "#",
      });
    });
  } else {
    fileElements.forEach((el) => {
      const speakerText = el.metadata?.attribution || el.attribution || "";
      presentations.push({
        title: el.title || el.metadata?.title || "",
        speakers: speakerText ? [speakerText] : [],
        pdfUrl: el.url || el.metadata?.url || "#",
      });
    });
  }

  return {
    conclusionPaper,
    presentations,
    presentationsTitle: secondTitle,
    footnote: footnoteText,
  };
};

// Helper function to extract highlight card data from a card
const extractHighlightCardData = (card) => {
  const storyElements = card?.['story-elements'] || [];
  return { storyElements };
};

// Helper function to extract key takeaways data from a card
const extractKeyTakeawaysData = (card) => {
  const storyElements = card?.['story-elements'] || [];
  const textElements = storyElements.filter((el) => el?.type === "text");
  return {
    takeaways: textElements.map(el => el.text).filter(Boolean),
  };
};

// Helper function to map speaker entity to speaker card format
const mapSpeakerToCard = (speaker, isConfirmed = false) => ({
  name: speaker?.['display-name'] || speaker?.name || "",
  designation: speaker?.designation || "",
  company: speaker?.company || "",
  imageUrl: speaker?.photo?.[0]?.url || "/icons/Group 333.png",
  linkedIn: speaker?.linkedin || "",
  isConfirmed,
  description: speaker?.description || "",
});

// Helper function to extract sponsors from card story-elements
const extractSponsorsFromCard = (card) => {
  const storyElements = card?.['story-elements'] || [];
  const imageGallery = storyElements.find((el) => el?.subtype === "image-gallery");
  const galleryElements = imageGallery?.['story-elements'] || [];

  return galleryElements.map((el) => ({
    name: el?.["alt-text"] || "",
    type: el?.title || "",
    imageS3Key: el?.["image-s3-key"] || "",
    imageMetadata: el?.["image-metadata"] || {},
    caption: el?.title || "",
  }));
};

// Helper function to extract agenda data from cards
const extractAgendaFromCards = (cards) => {
  // Filter cards that have agenda attribute
  const agendaCards = cards.filter((card) => card?.metadata?.attributes?.agenda?.[0]);
  const agendaCardsPresent = agendaCards?.length > 0;

  // Group cards by agenda day (day 0, day 1, day 2, etc.)
  const groupedByDay = {};

  agendaCards.forEach((card) => {
    const agendaDay = card?.metadata?.attributes?.agenda?.[0] || "";
    if (!groupedByDay[agendaDay]) {
      groupedByDay[agendaDay] = [];
    }
    groupedByDay[agendaDay].push(card);
  });

  // Extract Day 0 (arrival) and last day (departure) info
  const day0Cards = groupedByDay["arrival-card"] || [];
  const lastDayCards = groupedByDay["departure-card"] || [];
  const isArrivalCard = day0Cards?.length > 0;
  const isDepartureCard = lastDayCards?.length > 0;

  const arrivalCard = day0Cards[0];
  const departureCard = lastDayCards[0];

  const arrivalElement = arrivalCard?.['story-elements']?.find((el) => el?.subtype === "q-and-a");
  const departureElement = departureCard?.['story-elements']?.find((el) => el?.subtype === "q-and-a");

  const arrival = {
    date: arrivalElement?.metadata?.question || "",
    text: arrivalElement?.metadata?.answer || "",
  };

  const departure = {
    date: departureElement?.metadata?.question || "",
    text: departureElement?.metadata?.answer || "",
  };

  // Transform regular day cards (day 1, day 2, day 3)
  const days = [];

  // Process each day (skip day 0 and last day as they are handled separately)
  // Sort day keys to ensure proper order
  const dayKeys = Object.keys(groupedByDay)
    .filter((key) => key !== "arrival-card" && key !== "departure-card" && key !== "agenda-description" && key !== "agenda-attachment" && key !== "partners-agenda-attachment")
    .sort((a, b) => {
      const numA = parseInt(a.replace("day ", ""), 10);
      const numB = parseInt(b.replace("day ", ""), 10);
      return numA - numB;
    });

  dayKeys.forEach((dayKey) => {
    const dayCards = groupedByDay[dayKey];

    // First card should have the day header (date and title)
    const dayHeaderCard = dayCards[0];
    const dayHeaderElement = dayHeaderCard?.['story-elements']?.find((el) => el?.subtype === "q-and-a");

    const dayNumber = dayKey.replace("day ", "");
    const dayLabel = `DAY ${dayNumber}`;
    const date = dayHeaderElement?.metadata?.question || "";
    const title = dayHeaderElement?.metadata?.answer || "";
    // Process session cards (skip the first card as it's the header)
    const sessions = dayCards.slice(1).map((sessionCard) => {
      const storyElements = sessionCard?.['story-elements'] || [];

      // Extract imagetype attribute from session card metadata
      const imagetype = sessionCard?.metadata?.attributes?.imagetype?.[0] || "rounded";

      // Extract time and location from Q&A element
      const qaElement = storyElements.find((el) => el?.subtype === "q-and-a" || el?.subtype === "question");
      const time = qaElement?.metadata?.question || qaElement?.text || "";
      const location = qaElement?.metadata?.answer || "";

      // Extract session title from title element
      const titleElement = storyElements.find((el) => el?.type === "title");
      const sessionTitle = titleElement?.text || "";

      // Extract description from text elements
      const textElements = storyElements.filter((el) => el?.type === "text" && el?.subtype !== "q-and-a" && el?.subtype !== "question");
      const description = textElements?.[0]?.text || "";
      const note = textElements?.[1]?.text || "";

      // Extract speakers from image gallery
      const imageGallery = storyElements.find((el) => el?.subtype === "image-gallery");
      const speakerImages = imageGallery?.['story-elements'] || [];
      const speakers = speakerImages.map((img) => ({
        name: img?.title || "",
        designation: img?.["alt-text"] || "",
        imageUrl: img?.["image-s3-key"] || "",
        imageMetadata: img?.["image-metadata"] || {},
        caption: img?.title || "",
        attribution: img?.["image-attribution"] || "",
      }));

      //extract image elements
      const imageElement = storyElements.find((el) => el?.type === "image");
      const imgElement = isEmpty(imageElement) ? '' : {
        name: imageElement?.title || "",
        designation: imageElement?.["alt-text"] || "",
        imageUrl: imageElement?.["image-s3-key"] || "",
        imageMetadata: imageElement?.["image-metadata"] || {},
        caption: imageElement?.title || "",
        attribution: imageElement?.["image-attribution"] || "",
      };

      return {
        time,
        location,
        title: sessionTitle,
        description,
        note: note || undefined,
        speakers: speakers.length > 0 ? speakers : undefined,
        imagetype,
        imgElement,
      };
    }).filter((session) => session.time || session.title); // Filter out empty sessions

    // Only add days that have either sessions or are worth showing
    days.push({
      dayLabel,
      date,
      title,
      sessions,
    });
  });

  const agendaDescCard = groupedByDay["agenda-description"];
  const agendaAttachement = groupedByDay["agenda-attachment"];
  const agendaPartnerAttachement = groupedByDay["partners-agenda-attachment"];
  const agendaDesc = agendaDescCard?.[0]?.['story-elements']?.[0]?.text || '';
  const agendaAttachementFile = agendaAttachement?.[0]?.['story-elements']?.[0]?.url || '';
  const agendaPartnerAttachementFile = agendaPartnerAttachement?.[0]?.['story-elements']?.[0]?.url || '';

  return {
    arrival,
    departure,
    isArrivalCard,
    isDepartureCard,
    days,
    agendaDesc,
    agendaAttachementFile,
    agendaPartnerAttachementFile,
    agendaCardsPresent,
  };
};

const staticParticipantsData = {
  title: "Participants",
  linkText: "View all participants",
  imageUrl: "https://www.figma.com/api/mcp/asset/72640193-aaf5-4295-9c41-83e102ce849c",
};

// View types for the conference page
const VIEW_TYPES = {
  MAIN: 'main',
  PARTICIPANTS: 'participants',
  BOOK_SEAT: 'book_seat',
};

const ConferencePage = ({ story, subscriptionPlans, initRazorPayPayment, hasUserPurchasedStory }) => {
  const isMobile = useIsMobile();
  const [currentView, setCurrentView] = useState(VIEW_TYPES.MAIN);
  const [allParticipantsList, setAllParticipantsList] = useState([]);
  const [participantsDetails, setParticipantsDetails] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [isCurrentUserParticipant, setIsCurrentUserParticipant] = useState(false);
  const [isInternalUser, setIsInternalUser] = useState(false);
  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);
  const [isKeepMeInformedModalOpen, setIsKeepMeInformedModalOpen] = useState(false);
  const [isRsvpModalOpen, setIsRsvpModalOpen] = useState(false);

  const subscriberMetadata = useSelector((state) => get(state, ["subscriberMetadata"], null));
  const member = useSelector((state) => get(state, ["member"], null));
  const memberId = get(member, ["id"]);
  const storyTemplate = story?.['story-template'];

  // Update itemsPerPage based on window width
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      let newItemsPerPage = 20;
      if (width < 768) {
        newItemsPerPage = 10;
      } else if (width < 1024) {
        newItemsPerPage = 15;
      }
      if (newItemsPerPage !== itemsPerPage) {
        setItemsPerPage(newItemsPerPage);
      }
    };

    handleResize(); // Initial check
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [itemsPerPage]);

  const scrollToTop = () => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Sync state with URL hash
  useEffect(() => {
    const syncHashWithState = () => {
      if (typeof window !== "undefined") {
        const hash = window.location.hash;
        let newView = VIEW_TYPES.MAIN;

        if (hash === "#participants") {
          newView = VIEW_TYPES.PARTICIPANTS;
        } else if (hash === "#book-seat") {
          if (!hasUserPurchasedStory) {
            newView = VIEW_TYPES.BOOK_SEAT;
          } else {
            // Redirect to main view if user has already purchased
            if (typeof window !== "undefined") {
              window.history.pushState("", document.title, window.location.pathname + window.location.search);
            }
            newView = VIEW_TYPES.MAIN;
          }
        }

        if (newView !== currentView) {
          setCurrentView(newView);
          if (newView === VIEW_TYPES.PARTICIPANTS) {
            scrollToTop();
          }
        }
      }
    };

    syncHashWithState();
    window.addEventListener("hashchange", syncHashWithState);
    return () => window.removeEventListener("hashchange", syncHashWithState);
  }, [currentView, hasUserPurchasedStory]);

  // Safely extract story fields
  const { headline, subheadline, cards = [], metadata, sections = [] } = story || {};

  // Extract hero image from story
  const heroImageS3Key = story?.["hero-image-s3-key"] || "";
  const heroImageMetadata = story?.["hero-image-metadata"] || {};

  // Get section name for breadcrumb
  const sectionName = sections?.[0]?.name || "";

  // Extract event info from metadata
  const { when, arrival, departure, time } = metadata || {};
  const whereLocation = metadata?.where || "";
  const sessionHeroimageCard = cards.find((card) => card?.metadata?.attributes?.individualeventcards?.[0] === "session hero image") || {};
  const sessionHeroImageCardStoryElements = sessionHeroimageCard?.['story-elements'] || [];
  const sessionDesktopHeroImage = sessionHeroImageCardStoryElements?.[0]?.["image-s3-key"] || "";
  const sessionMobileHeroImage = sessionHeroImageCardStoryElements?.[1]?.["image-s3-key"] || "";
  const sessionDesktopHeroImageMetadata = sessionHeroImageCardStoryElements?.[0]?.["image-metadata"] || {};
  const sessionMobileHeroImageMetadata = sessionHeroImageCardStoryElements?.[1]?.["image-metadata"] || {};
  const feedBackLink = metadata?.["feedback-link"] || "";

  // Build heroBannerData dynamically
  const getHeroImage = () => {
    if (storyTemplate === "session") {
      return isMobile ? sessionMobileHeroImage : sessionDesktopHeroImage;
    }
    return heroImageS3Key;
  };

  const getHeroImageMetadata = () => {
    if (storyTemplate === "session") {
      return isMobile ? sessionMobileHeroImageMetadata : sessionDesktopHeroImageMetadata;
    }
    return heroImageMetadata;
  };

  const heroBannerData = {
    imageUrl: getHeroImage(),
    heroImageS3Key: getHeroImage(),
    heroImageMetadata: getHeroImageMetadata(),
    breadcrumbs: [sectionName, headline].filter(Boolean),
    title: headline || "",
    subtitle: subheadline || "",
    eventInfo: {
      when: when || "",
      arrival: arrival || "",
      where: whereLocation || "",
      departure: departure || "",
      time: time || "",
      sessionForum: metadata?.["session-forum"] || "",
    },
    storyTemplate,
  };

  // Get text content from first card for LargeTextParagraph
  const firstCard = cards[0] || {};
  const firstCardStoryElements = firstCard?.['story-elements'] || [];
  const firstCardTextElement = firstCardStoryElements.find((el) => el?.type === "text");
  const largeTextContent = firstCardTextElement?.text
    ? styleBoldText(firstCardTextElement.text)
    : "";

  // Find card with individualeventcards as "venue summary"
  const venueCard = cards.find((card) => card?.metadata?.attributes?.individualeventcards?.[0] === "venue summary") || {};
  const venueImageS3Key = venueCard?.['story-elements']?.[0]?.["image-s3-key"] || "";
  const venueImageMetadata = venueCard?.['story-elements']?.[0]?.["image-metadata"] || {};

  // Find card with individualeventcards as "participants"
  const participantsCard = cards.find((card) => card?.metadata?.attributes?.individualeventcards?.[0] === "participants") || {};
  const participantsStoryElements = participantsCard?.['story-elements'] || [];
  const participantsHeading = participantsStoryElements.filter((el) => el?.type === "title");
  const participantsContent = participantsStoryElements.filter((el) => el?.type === "text");
  const participantsImageElement = participantsStoryElements.find((el) => el?.type === "image") || {};
  const participantsCtaHeading = participantsHeading?.[0]?.text || "";
  const participantsDescription = participantsContent?.[0]?.text || "";
  const participantsImageS3Key = participantsImageElement?.["image-s3-key"] || "";
  const participantsImageMetadata = participantsImageElement?.["image-metadata"] || {};
  const participantsImageCaption = participantsImageElement?.title || "";

  // ad banner
  const adbannerCard = cards?.find((card) => card?.metadata?.attributes?.individualeventcards?.[0] === "ad image") || {};
  const adbanner = adbannerCard?.['story-elements']?.[0];

  // Find cards with individualeventcards as "sponsors" (first = principal, second = other)
  const sponsorCards = cards.filter((card) => card?.metadata?.attributes?.individualeventcards?.[0] === "sponsors");
  const [principalSponsorsCard = {}, otherSponsorsCard = {}] = sponsorCards;
  const principalSponsors = extractSponsorsFromCard(principalSponsorsCard);
  const otherSponsors = extractSponsorsFromCard(otherSponsorsCard);

  // Memoize speakers calculation to avoid re-renders on itemsPerPage change
  const allSpeakers = React.useMemo(() => {
    // Extract speakers from story-attributes (metadata)
    const storyAttributes = metadata?.['story-attributes'] || {};
    const {
      confirmedspeakers: confirmedSpeakersData = [],
      speakers,
      starspeakers,
    } = storyAttributes;

    const speakersData = speakers || starspeakers || [];

    // Get IDs of confirmed speakers to filter out duplicates
    const confirmedSpeakerIds = new Set(confirmedSpeakersData.map((speaker) => speaker?.id));

    // Filter out speakers that are already in confirmedSpeakersData
    const uniqueSpeakersData = speakersData.filter((speaker) => !confirmedSpeakerIds.has(speaker?.id));

    // Map speakers to card format - confirmed speakers first, then other speakers
    const confirmedSpeakersMapped = confirmedSpeakersData.map((speaker) => mapSpeakerToCard(speaker, true));
    const otherSpeakersMapped = uniqueSpeakersData.map((speaker) => mapSpeakerToCard(speaker, false));

    // Combine all speakers - confirmed first, then others
    return [...confirmedSpeakersMapped, ...otherSpeakersMapped];
  }, [metadata]);

  // Extract event participants data
  const storyAttributes = metadata?.['story-attributes'] || {};
  const isPreviousEvent = storyAttributes?.ispreviousevent?.[0] === "true";
  const { eventparticipants = [] } = storyAttributes;
  const eventParticipantsData = eventparticipants?.[0] || {};
  const internalUsers = eventparticipants?.[1] || {};

  // Get the first event participant's id (slug)
  const eventEntitySlug = eventParticipantsData?.id || "";
  // Get the internal users entity id (slug)
  const internalUsersEntitySlug = internalUsers?.id || "";

  // key takeaways card for sessions
  const keyTakeawaysCard = cards.find((card) => card?.metadata?.attributes?.individualeventcards?.[0] === "key takeaways") || {};
  const keyTakeawaysData = extractKeyTakeawaysData(keyTakeawaysCard);

  // Helper function to map participant entity to card format (same as speakers)
  const mapParticipantToCard = (participant = {}) => {
    const {
      "display-name": displayName,
      designation = "",
      company = "",
      photo = [],
      linkedin,
      "linkedin-url": linkedinUrlAlt,
    } = participant;
    return {
      name: displayName || "",
      designation,
      company,
      imageUrl: photo?.[0]?.url || "/icons/Group 333.png",
      linkedIn: linkedin || linkedinUrlAlt || "",
    };
  };

  // Function to fetch participant details for current page
  const fetchParticipantDetails = useCallback(async (participantsList = [], page = 1) => {
    if (!participantsList.length) {
      setParticipantsDetails([]);
      return;
    }

    const startIndex = (page - 1) * itemsPerPage;
    const currentPageParticipants = participantsList.slice(startIndex, startIndex + itemsPerPage);

    const detailPromises = currentPageParticipants.map(async ({ slug }) => {
      if (!slug) return mapParticipantToCard({});

      try {
        const response = await fetch(`/api/v1/entities/${slug}`);
        const data = await response.json();
        return mapParticipantToCard(data?.entity);
      } catch {
        return mapParticipantToCard({});
      }
    });

    const details = await Promise.all(detailPromises);
    setParticipantsDetails(details);
  }, [itemsPerPage]);

  // Fetch the main entity list on mount
  useEffect(() => {
    if (eventEntitySlug) {
      fetch(`/api/v1/entities/${eventEntitySlug}`)
        .then((response) => response.json())
        .then((data) => {
          const participants = data?.entity?.participants || [];
          setAllParticipantsList(participants);

          // Check if current user is a participant 
          if (memberId && participants.length > 0) {
            const isParticipant = participants.some(p => p.name === member.email);
            setIsCurrentUserParticipant(isParticipant);
          } else {
            setIsCurrentUserParticipant(false);
          }

          // Fetch details for first page
          if (participants.length > 0) {
            fetchParticipantDetails(participants, 1);
          }
        })
        .catch((error) => {
          console.error("Error fetching entity:", error);
        });
    }
    if (internalUsersEntitySlug) {
      fetch(`/api/v1/entities/${internalUsersEntitySlug}`)
        .then((response) => response.json())
        .then((data) => {
          const internalUsers = data?.entity?.participants || [];
          // Check if current user is a participant 
          if (memberId && internalUsers.length > 0) {
            const isParticipant = internalUsers.some(p => p.name === member.email);
            setIsInternalUser(isParticipant);
          } else {
            setIsInternalUser(false);
          }
        })
        .catch((error) => {
          console.error("Error fetching entity:", error);
        });
    }
  }, [eventEntitySlug, fetchParticipantDetails, memberId]);

  // Handle pagination change
  const handleParticipantsPageChange = (page) => {
    setCurrentPage(page);
    fetchParticipantDetails(allParticipantsList, page);
  };

  // Re-fetch details when itemsPerPage changes to keep view consistent
  useEffect(() => {
    if (allParticipantsList.length > 0) {
      // If the current page is no longer valid with the new itemsPerPage, we might need to adjust it
      // but for now just re-fetch for the current page
      fetchParticipantDetails(allParticipantsList, currentPage);
    }
  }, [itemsPerPage, allParticipantsList, currentPage, fetchParticipantDetails]);

  // Check URL parameters for modal opening
  useEffect(() => {
    if (typeof window !== "undefined") {
      const urlParams = new URLSearchParams(window.location.search);
      const openModal = urlParams.get('openModal');

      if (openModal === 'keepMeInformed') {
        setIsKeepMeInformedModalOpen(true);
        // Clean up URL parameter
        const newUrl = new URL(window.location);
        newUrl.searchParams.delete('openModal');
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, []);

  // Find card with individualeventcards as "star speakers"
  const starSpeakersCard = cards.find((card) => card?.metadata?.attributes?.individualeventcards?.[0] === "star speakers") || {};
  const starSpeakersCardData = extractHighlightCardDataWithGallery(starSpeakersCard);

  // Find card with individualeventcards as "location" for Colombo highlight card
  const locationCard = cards.find((card) => card?.metadata?.attributes?.individualeventcards?.[0] === "location") || {};
  const locationCardData = extractHighlightCardData(locationCard);

  // Find card with individualeventcards as "special sessions"
  const specialSessionsCard = cards.find((card) => card?.metadata?.attributes?.individualeventcards?.[0] === "special sessions") || {};
  const specialSessionsCardData = extractHighlightCardData(specialSessionsCard);

  // Find card with individualeventcards as "participating companies"
  const participatingCompaniesCard = cards.find((card) => card?.metadata?.attributes?.individualeventcards?.[0] === "participating companies") || {};
  const participatingCompaniesCardData = extractHighlightCardData(participatingCompaniesCard);

  // Build highlight cards data - dynamic cards
  const highlightCardsData = [
    {
      storyElements: starSpeakersCardData.storyElements,
      galleryImages: starSpeakersCardData.galleryImages,
      showCounter: starSpeakersCardData.galleryImages.length > 1,
    },
    {
      storyElements: specialSessionsCardData.storyElements,
    },
    {
      storyElements: participatingCompaniesCardData.storyElements,
    },
    {
      storyElements: locationCardData.storyElements,
    },
  ].filter((card) => card?.storyElements && card.storyElements.length > 0);

  const imageGalleryCard = cards.find((card) => {
    const individualAttr = (card?.metadata?.attributes?.individualeventcards?.[0] || "").trim();
    return individualAttr === "previous event image gallery";
  }) || {};

  const imageGalleryCardData = extractHighlightCardDataWithGallery(imageGalleryCard);

  const outcomeCards = cards.filter((card) => {
    const individualAttr = (card?.metadata?.attributes?.individualeventcards?.[0] || "").trim();
    return individualAttr === "previous event outcomes";
  });

  const outcomeCardData = outcomeCards.reduce(
    (acc, card) => {
      const data = extractOutcomeCardData(card);
      return {
        conclusionPaper: acc.conclusionPaper || data.conclusionPaper,
        presentations: [...acc.presentations, ...data.presentations],
        presentationsTitle: acc.presentationsTitle || data.presentationsTitle,
        footnote: data.footnote || acc.footnote,
      };
    },
    { conclusionPaper: null, presentations: [], presentationsTitle: "", footnote: "" }
  );

  // Extract metadata fields
  const {
    duration,
    where,
    venue,
    address,
    contact,
    "contact-address": contactAddress,
    "contact-phone": contactPhone,
    "corporate-address": corporateAddress,
    "maps-url": mapUrl,
    "session-forum": sessionForum,
  } = metadata || {};

  // Build eventDetailsData from story
  const eventDetailsData = {
    eventTitle: headline,
    eventSubtitle: subheadline,
    eventDate: duration || when,
    eventLocation: where,
    venue,
    venueAddress: address,
    contact,
    corporateAddress,
    contactAddress,
    contactPhone,
    mapUrl,
  };

  // Extract agenda data from cards
  const agendaData = extractAgendaFromCards(cards);

  // Render venue image using ResponsiveImage or fallback
  const venueImage = venueImageS3Key && (
    <ResponsiveImage
      slug={venueImageS3Key}
      metadata={venueImageMetadata}
      aspectRatio={[16, 9]}
      defaultWidth={480}
      widths={[336, 480, 640]}
      imgParams={{ auto: ["format", "compress"] }}
      alt={venue || "Venue"}
    />
  );

  // Render participants image using ResponsiveImage or fallback
  const participantsImage = participantsImageS3Key ? (
    <ResponsiveImage
      slug={participantsImageS3Key}
      metadata={participantsImageMetadata}
      aspectRatio={[16, 9]}
      defaultWidth={640}
      widths={[480, 640, 960]}
      imgParams={{ auto: ["format", "compress"] }}
      alt="Participants"
    />
  ) : null;

  const handleViewAllParticipants = () => {
    setCurrentView(VIEW_TYPES.PARTICIPANTS);
    if (typeof window !== "undefined") {
      window.location.hash = "participants";
    }
    scrollToTop();
  };

  const handleBackFromParticipants = () => {
    setCurrentView(VIEW_TYPES.MAIN);
    if (typeof window !== "undefined" && window.location.hash === "#participants") {
      window.history.pushState("", document.title, window.location.pathname + window.location.search);
    }
  };

  const handleShowBookSeat = () => {
    if (hasUserPurchasedStory) {
      return; // Disable booking if user has already purchased
    }
    setCurrentView(VIEW_TYPES.BOOK_SEAT);
    if (typeof window !== "undefined") {
      window.location.hash = "book-seat";
    }
    scrollToTop();
  };

  const handleBackFromBookSeat = () => {
    setCurrentView(VIEW_TYPES.MAIN);
    if (typeof window !== "undefined" && window.location.hash === "#book-seat") {
      window.history.pushState("", document.title, window.location.pathname + window.location.search);
    }
  };

  const handleSignIn = () => {
    setIsSignInModalOpen(true);
  };

  const handleCloseSignInModal = () => {
    setIsSignInModalOpen(false);
  };

  const handleKeepMeInformed = () => {
    setIsKeepMeInformedModalOpen(true);
  };

  const handleRsvpButtonClick = () => {
    setIsRsvpModalOpen(true);
  };

  const handleKeepMeInformedSubmit = async (formData) => {

    const clientManagerEmail = get(subscriberMetadata, ["client-manager-email"], "");

    const requestData = {
      eventName: headline || "Event",
      eventDate: duration || when || "TBD",
      eventLocation: where || "TBD",
      venue: venue || "TBD",
      venueAddress: address || "TBD",
      memberData: formData,
      clientManagerEmail,
    };

    try {
      const response = await fetch("/keep-me-informed-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      });

      const result = await response.json();

      if (response.ok) {
        alert("Thank you for your interest! We'll keep you informed about this event.");
      } else {
        console.error("Failed to send email:", result.error);
        throw new Error(result.error || "Failed to send email");
      }
    } catch (error) {
      console.error("Error sending keep me informed email:", error);
      alert("Sorry, there was an error processing your request. Please try again later.");
      throw error;
    }
  };

  const handleCloseKeepMeInformedModal = () => {
    setIsKeepMeInformedModalOpen(false);
  };

  const handleGiveFeedbackClick = () => {
    if (feedBackLink) {
      window.open(feedBackLink, "_blank");
    }
  };

  const participantsNavTitle = (headline || "").trim();

  // Helper function to build dynamic navigation data based on rendered sections
  const buildDynamicNavData = (baseNavData, isPrevious = false) => {
    const dynamicLinks = [];
    const baseLinks = baseNavData.links || [];

    // Check each section and add corresponding nav link if section is rendered
    baseLinks.forEach(link => {
      const linkText = link.text;
      let shouldInclude = false;

      switch (linkText) {
        case "Overview":
          // Overview is always present if there's large text content
          shouldInclude = Boolean(largeTextContent);
          break;

        case "Key Takeaways":
          // Only for sessions and if takeaways exist
          shouldInclude = storyTemplate === "session" && Boolean(keyTakeawaysData.takeaways?.length);
          break;

        case "Speakers":
          // If there are speakers
          shouldInclude = Boolean(allSpeakers?.length);
          break;

        case "Agenda":
          // If agenda data is present
          shouldInclude = Boolean(agendaData?.agendaCardsPresent);
          break;

        case "Sponsors":
          // If there are sponsors
          shouldInclude = Boolean(principalSponsors?.length || otherSponsors?.length);
          break;

        case "Participants":
          // If there are participants
          shouldInclude = Boolean(allParticipantsList.length);
          break;

        case "Venue & Contact":
        case "Venue":
          // If venue details exist
          shouldInclude = !isPreviousEvent && Boolean(venue || address || contact || corporateAddress);
          break;

        case "Outcomes":
          // For previous events, if outcomes exist
          shouldInclude = isPrevious && Boolean(outcomeCardData?.conclusionPaper?.url || outcomeCardData?.presentations.length > 0 || outcomeCardData?.footnote);
          break;

        default:
          // Include unknown links by default
          shouldInclude = true;
          break;
      }

      if (shouldInclude) {
        dynamicLinks.push(link);
      }
    });

    // Build dynamic buttons based on authentication state and story template
    let dynamicButtons = [...(baseNavData.buttons || [])];

    // For session template when user is not signed in, use sign in and become a member buttons
    if (storyTemplate === "session" && !member && !isPrevious) {
      dynamicButtons = [
        { text: "Sign In", variant: "sign-in", onClick: handleSignIn },
        { text: "Become a member", variant: "primary", onClick: () => window.location.href = "/become-a-member" },
      ];
    }

    return {
      ...baseNavData,
      links: dynamicLinks,
      buttons: dynamicButtons
    };
  };

  // Helper function to render view content based on current view
  const renderViewContent = () => {
    switch (currentView) {
      case VIEW_TYPES.PARTICIPANTS:
        return (
          <>
            <LocalNavBar
              {...buildDynamicNavData(storyTemplate === "session" ? SESSIONS_LOCAL_NAV_DATA : CONFERENCE_LOCAL_NAV_DATA)}
              isParticipantsView={true}
              participantsTitle={participantsNavTitle}
              title={participantsNavTitle}
              onBack={handleBackFromParticipants}
              storyTemplate={storyTemplate}
              eventInfo={heroBannerData.eventInfo}
              isRsvpModalOpen={isRsvpModalOpen}
              member={member}
            />
            <ParticipantsListSection
              title="Participants"
              participants={participantsDetails}
              allParticipants={allParticipantsList}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              onPageChange={handleParticipantsPageChange}
              onBack={handleBackFromParticipants}
            />
          </>
        );

      case VIEW_TYPES.BOOK_SEAT:
        return (
          <>
            <LocalNavBar
              {...{
                links: [],
                buttons: [
                  { text: "Keep me informed", variant: "outline", onClick: handleKeepMeInformed }
                ]
              }}
              isParticipantsView={true}
              participantsTitle={participantsNavTitle}
              title={participantsNavTitle}
              onBack={handleBackFromBookSeat}
              showStickyButtons={false}
              storyTemplate={storyTemplate}
              isRsvpModalOpen={isRsvpModalOpen}
              member={member}
            />
            <BookSeatSection
              onSignIn={handleSignIn}
              hasUserPurchasedStory={hasUserPurchasedStory}
              subscriptionPlans={subscriptionPlans}
              initRazorPayPayment={initRazorPayPayment}
              story={story}
            />
          </>
        );

      case VIEW_TYPES.MAIN:
      default:
        if (isPreviousEvent) {
          return (
            <>
              <LocalNavBar
                {...buildDynamicNavData(storyTemplate === "session" ? PREVIOUS_SESSIONS_LOCAL_NAV_DATA : PREVIOUS_CONFERENCE_LOCAL_NAV_DATA, true)}
                storyTemplate={storyTemplate}
                isParticipantsView={false}
                participantsTitle={participantsNavTitle}
                title={participantsNavTitle}
                eventInfo={heroBannerData.eventInfo}
                isPreviousEvent={true}
                isRsvpModalOpen={isRsvpModalOpen}
                feedBackLink={feedBackLink}
                onGiveFeedbackClick={handleGiveFeedbackClick}
                member={member}
              />
              {largeTextContent && <LargeTextParagraph storyTemplate={storyTemplate}>{largeTextContent}</LargeTextParagraph>}
              {storyTemplate === "session" && keyTakeawaysData.takeaways.length > 0 && (<KeyTakeawaysCard takeaways={keyTakeawaysData.takeaways} />)}
              {imageGalleryCardData?.hasGallery && <EventGallery
                galleryImages={imageGalleryCardData.galleryImages}
                morePhotosUrl={imageGalleryCardData.morePhotosUrl}
                morePhotosText={imageGalleryCardData.morePhotosText}
                openInNewTab={imageGalleryCardData.openInNewTab}
                useBlackBackground={true}
              />}
              {Boolean(allSpeakers?.length) && <SpeakersSection title="Speakers" speakers={allSpeakers} storyTemplate={storyTemplate} />}
              {agendaData?.agendaCardsPresent && <AgendaSection
                title="Agenda"
                arrivalDate={agendaData.arrival.date}
                arrivalText={agendaData.arrival.text}
                departureDate={agendaData.departure.date}
                departureText={agendaData.departure.text}
                days={agendaData.days}
                agendaDescription={agendaData.agendaDesc}
                onParticipantsClick={handleViewAllParticipants}
                downloadLink={agendaData.agendaAttachementFile}
                isArrivalCard={agendaData?.isArrivalCard}
                isDepartureCard={agendaData?.isDepartureCard}
                partnerDownloadLink={agendaData.agendaPartnerAttachementFile}
              />}
              {Boolean(principalSponsors?.length || otherSponsors?.length) && <SponsorsSection title="Sponsors" principalSponsors={principalSponsors} otherSponsors={otherSponsors} />}
              {adbanner && storyTemplate !== "session" && <AdBanner adbanner={adbanner} height="328px" variant="large" />}
              {(outcomeCardData?.conclusionPaper?.url || outcomeCardData?.presentations.length > 0 || outcomeCardData?.footnote) && <EventOutcomes
                footnote={outcomeCardData?.footnote}
                conclusionPaper={outcomeCardData?.conclusionPaper}
                presentations={outcomeCardData?.presentations}
                showReadNow={storyTemplate === "session" || isCurrentUserParticipant || isInternalUser}
                presentationsTitle={outcomeCardData?.presentationsTitle}
              />}
            </>
          );
        } else {
          return (
            <>
              <LocalNavBar
                {...buildDynamicNavData(storyTemplate === "session" ? SESSIONS_LOCAL_NAV_DATA : CONFERENCE_LOCAL_NAV_DATA)}
                isParticipantsView={false}
                participantsTitle={participantsNavTitle}
                title={participantsNavTitle}
                onBookSeatClick={handleShowBookSeat}
                onKeepMeInformedClick={handleKeepMeInformed}
                onRsvpClick={handleRsvpButtonClick}
                hasUserPurchasedStory={hasUserPurchasedStory}
                showStickyButtons={true}
                storyTemplate={storyTemplate}
                eventInfo={heroBannerData.eventInfo}
                isRsvpModalOpen={isRsvpModalOpen}
                member={member}
              />
              {largeTextContent && <LargeTextParagraph storyTemplate={storyTemplate}>{largeTextContent}</LargeTextParagraph>}
              {storyTemplate === "session" && keyTakeawaysData.takeaways.length > 0 && (<KeyTakeawaysCard takeaways={keyTakeawaysData.takeaways} />)}
              {storyTemplate !== "session" && Boolean(highlightCardsData.length) && <HighlightCardSection cards={highlightCardsData} />}
              {Boolean(allSpeakers?.length) && <SpeakersSection title="Speakers" speakers={allSpeakers} storyTemplate={storyTemplate} />}
              {agendaData?.agendaCardsPresent && <AgendaSection
                title="Agenda"
                arrivalDate={agendaData.arrival.date}
                arrivalText={agendaData.arrival.text}
                departureDate={agendaData.departure.date}
                departureText={agendaData.departure.text}
                days={agendaData.days}
                agendaDescription={agendaData.agendaDesc}
                onParticipantsClick={handleViewAllParticipants}
                downloadLink={agendaData.agendaAttachementFile}
                isArrivalCard={agendaData?.isArrivalCard}
                isDepartureCard={agendaData?.isDepartureCard}
                partnerDownloadLink={agendaData.agendaPartnerAttachementFile}
              />}
              {Boolean(principalSponsors?.length || otherSponsors?.length) && <SponsorsSection title="Sponsors" principalSponsors={principalSponsors} otherSponsors={otherSponsors} />}
              {adbanner && storyTemplate !== "session" && <AdBanner adbanner={adbanner} height="328px" variant="large" />}
              {Boolean(allParticipantsList.length) && <ParticipantsSection
                ctaHeading={participantsCtaHeading}
                description={participantsDescription}
                title={staticParticipantsData.title}
                linkText={staticParticipantsData.linkText}
                imageUrl={staticParticipantsData.imageUrl}
                backgroundImage={participantsImage}
                imageCaption={participantsImageCaption}
                onViewAllClick={handleViewAllParticipants}
                participantsCount={allParticipantsList.length}
                onBookSeatClick={handleShowBookSeat}
                hasUserPurchasedStory={hasUserPurchasedStory}
              />}
              <EventDetailsSection
                {...eventDetailsData}
                venueImage={venueImage}
                onBookSeatClick={handleShowBookSeat}
                onKeepMeInformedClick={handleKeepMeInformed}
                onRsvpClick={handleRsvpButtonClick}
                hasUserPurchasedStory={hasUserPurchasedStory}
                storyTemplate={storyTemplate}
              />
            </>
          );
        }
    }
  };

  return (
    <div styleName="conference-page">
      {currentView !== VIEW_TYPES.BOOK_SEAT && adbanner && <AdBanner adbanner={adbanner} height="328px" />}
      <HeroBanner {...heroBannerData} />
      {renderViewContent()}
      <SignInModal isOpen={isSignInModalOpen} onClose={handleCloseSignInModal} />
      <KeepMeInformedModal
        isOpen={isKeepMeInformedModalOpen}
        onClose={handleCloseKeepMeInformedModal}
        onSubmit={handleKeepMeInformedSubmit}
      />
      <RsvpModal
        isOpen={isRsvpModalOpen}
        onClose={() => setIsRsvpModalOpen(false)}
        sessionForum={sessionForum}
        story={story}
      />
    </div>
  );
};

export { ConferencePage };
